import {
  PROVIDER_SEND_TURN_MAX_IMAGE_BYTES,
  SNAP_SHOT_PAGE_URL_MAX_CHARS,
  SNAP_SHOT_WINDOW_TITLE_MAX_CHARS,
  type ScopedThreadRef,
  type SnapShotSource,
} from "@afar-research/contracts";

import { readFileAsDataUrl } from "~/components/ChatView.logic";
import { stackedThreadToast, toastManager } from "~/components/ui/toast";
import { compressImageToByteLimit } from "~/lib/imageCompression";
import { attachSnapShotToDraft } from "~/lib/snapShotDraft";
import { randomUUID } from "~/lib/utils";
import { commandFailureMessage } from "~/rpc/transportError";

import { canvasPng, captureFileName, saveBlob } from "./streamedCapture";

/** What a Cloud Workspace SnapShot shows: the Cloud Display, or one Collaborative Browser page. */
export type CloudSnapShotSubject =
  | { readonly kind: "cloud-display"; readonly environmentLabel: string | null }
  | { readonly kind: "browser"; readonly pageUrl: string; readonly pageTitle: string | null };

const windowTitle = (title: string | null) =>
  (title ?? "").trim().slice(0, SNAP_SHOT_WINDOW_TITLE_MAX_CHARS);

function snapShotSource(subject: CloudSnapShotSubject, capturedAt: Date): SnapShotSource {
  const base = { kind: "snap-shot", capturedAt: capturedAt.toISOString() } as const;
  if (subject.kind === "cloud-display") {
    return {
      ...base,
      appName: "Cloud Display",
      windowTitle: windowTitle(subject.environmentLabel),
    };
  }
  const pageUrl = subject.pageUrl.trim().slice(0, SNAP_SHOT_PAGE_URL_MAX_CHARS);
  return {
    ...base,
    appName: "Collaborative Browser",
    windowTitle: windowTitle(subject.pageTitle),
    ...(pageUrl ? { pageUrl } : {}),
  };
}

/**
 * Takes a SnapShot of a Cloud Workspace panel as this viewer sees it, from the panel's own
 * canvas, and attaches it to the thread's draft (ADR-0137). The toast that confirms it also
 * offers to save the image to this computer.
 */
export async function takeCloudSnapShot(
  threadRef: ScopedThreadRef,
  canvas: HTMLCanvasElement,
  subject: CloudSnapShotSubject,
): Promise<void> {
  const capturedAt = new Date();
  const fileName =
    subject.kind === "browser"
      ? captureFileName(subject.pageUrl, "png", capturedAt)
      : captureFileName("", "png", capturedAt, "cloud-display");
  try {
    const png = await canvasPng(canvas);
    const compressed = await compressImageToByteLimit(
      new File([png], fileName, { type: "image/png" }),
      PROVIDER_SEND_TURN_MAX_IMAGE_BYTES,
    );
    if (!compressed.ok) throw new Error("The capture is too large to attach.");
    await attachSnapShotToDraft(threadRef, {
      id: randomUUID(),
      file: compressed.file,
      dataUrl: await readFileAsDataUrl(compressed.file),
      source: snapShotSource(subject, capturedAt),
    });
    toastManager.add(
      stackedThreadToast({
        type: "success",
        title: "SnapShot added to the draft",
        actionProps: { children: "Save image", onClick: () => saveBlob(png, fileName) },
        data: {
          secondaryActionProps: {
            children: "Copy image",
            onClick: () =>
              void navigator.clipboard
                ?.write([new ClipboardItem({ "image/png": png })])
                .catch(() => undefined),
          },
          secondaryActionVariant: "outline",
        },
      }),
    );
  } catch (error) {
    toastManager.add({
      type: "error",
      title: "Unable to take a SnapShot",
      description: commandFailureMessage(error, "An error occurred."),
    });
  }
}
